import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { faLightbulb } from "@fortawesome/free-regular-svg-icons";
import { useSelector } from "react-redux";
import axios from "axios";
import { RootState } from "../../../redux/store";
import { webURL } from "../../../services/API";
import { Main } from "./style";
import { Clock, Pause, Play, X } from "lucide-react";
import { Select } from "../../form/select";
import timezonesData from "../../../services/data/timezones.json";
import { selectDataType } from "../../../services/types";
import { createPortal } from "react-dom";
import { PopupOverlay } from "../popupOverlay";

export function LoopProject(
   { project, setProject, handleClose }: { project: projectType, setProject: React.Dispatch<React.SetStateAction<projectType | null>>, handleClose: () => void }
) {
   const userToken = useSelector((state: RootState) => state.user.data?.token);
   const [inputs, setInputs] = React.useState({ hours: '', minutes: '', seconds: '', zoneHour: '', zoneMinute: '' });
   const [values, seValues] = React.useState({
      minutes: project.loop.value,
      isBackground: project.loop.isBackground, 
      starting: project.loop.starting,
      timezone: project.loop.timezone
   });
   const [timezones, setTimezones] = React.useState<selectDataType[]>([]);

   // ---
   React.useEffect(() => {
      const totalSeconds = Math.floor(project.loop.value * 60);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      setInputs({ 
         hours: hours ? hours.toString() : '', 
         minutes: minutes ? minutes.toString() : '', 
         seconds: seconds ? seconds.toString() : '', 
         zoneHour: project.loop.timezone?.hour ? project.loop.timezone.hour.toString() : '', 
         zoneMinute: project.loop.timezone?.minute ? project.loop.timezone.minute.toString() : ''
      });
      setTimezones(timezonesData.map((name: string, index: number) => ({ 
         id: index+1, name, isActive: project.loop.timezone?.name ? project.loop.timezone.name === name : index === 0
      })));
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   const handleChange = (type: 'time' | 'zone' | 'background' | 'starting', value?: any, more?: any) => {
      if (type === 'time') {
         if (/^\d*$/.test(value)) {
            const data = { ...inputs, [more]: value };
            let minutes = 0;
            
            if (data.hours) minutes += parseInt(data.hours) * 60;
            if (data.minutes) minutes += parseInt(data.minutes);
            if (data.seconds) minutes += parseInt(data.seconds) / 60;
            setInputs({ ...data, zoneHour: '', zoneMinute: '' });
            seValues(values => ({ ...values, minutes, timezone: { ...values.timezone as any, hour: 0, minute: 0 }, starting: false })); 
         }
         
      } else if (type === 'zone') {
         if (/^\d*$/.test(value)) {
            setInputs(inputs => ({ ...inputs, hours: '', minutes: '', seconds: '', [more]: value })); 
            const key = more === 'zoneHour' ? 'hour' : 'minute';
            seValues(values => ({ 
               ...values, 
               minutes: 0,
               timezone: { ...values.timezone as any, name: timezones.find(i => i.isActive)?.name as string, [key]: parseInt(value) || 0 }, 
               starting: false 
            })); 
         }

      } else if (type === 'background') {
         seValues(values => ({ ...values, isBackground: !values.isBackground, starting: false })); 

      } else {
         const starting = !values.starting;
         const update = { ...values, value: values.minutes || 0, starting };

         seValues(values => ({ ...values, starting }));
         axios.put(`${webURL}/projects`, { isWorkflow: true, id: project._id, loop: update }, { headers: { 'x-auth-token': userToken }});
         // @ts-ignore
         setProject((project) => ({ ...project, loop: update }));
      }

      if (type !== 'starting' && project.loop.starting) { // @ts-ignore
         setProject((project) => ({ ...project, loop: { ...project.loop, starting: false }}));
         axios.put(`${webURL}/projects`, { isWorkflow: true, id: project._id, loop: { ...values, value: values.minutes || 0, starting: false }}, { headers: { 'x-auth-token': userToken }});
      }
   }

   const portalTarget = typeof document !== "undefined" ? document.body : null;
   if (!portalTarget) return null;

   return createPortal(
      <PopupOverlay onClick={ handleClose }>
         <Main onClick={ e => e.stopPropagation() }>
            <div className="close" onClick={ handleClose }>
               <X size={ 19 } />
            </div>
            <div className="content">
               <div className="title">
                  <div className="icon"><Clock size={ 20 } /></div>
                  <h3>Repeat running workflow</h3>
               </div>
               <div className="time">
                  <input placeholder="Hours" value={ inputs.hours || '' } onChange={ e => handleChange('time', e.target.value, 'hours') } />
                  <input placeholder="Minutes" value={ inputs.minutes || '' } onChange={ e => handleChange('time', e.target.value, 'minutes') } />
                  <input placeholder="Seconds" value={ inputs.seconds || '' } onChange={ e => handleChange('time', e.target.value, 'seconds') } />
               </div>
               <div className="tip first">
                  <FontAwesomeIcon icon={ faLightbulb } />
                  <span>The time interval in which the scenario should be repeated</span>
               </div>
               <div className="tip last">
                  <FontAwesomeIcon icon={ faLightbulb } />
                  <span>Total time must be at least greater than one minute</span>
               </div>
               <div className="or">Or run at a specific time during the day (24-hour system)</div>
               <div className="time tiemzone">
                  <Select 
                     data={ timezones } 
                     handleSelectItem={ (id) => setTimezones(items => items.map(item => ({ ...item, isActive: item.id === id }))) } 
                     isSmall={ true } 
                  />
                  <input placeholder="Hour" value={ inputs.zoneHour || '' } onChange={ e => handleChange('zone', e.target.value, 'zoneHour') } />
                  <input placeholder="Minute" value={ inputs.zoneMinute || '' } onChange={ e => handleChange('zone', e.target.value, 'zoneMinute') } />
               </div>
               <div className="background" onClick={ _=> handleChange('background') }>
                  <div style={{ backgroundColor: values.isBackground ? 'rgba(14, 116, 144, 1)' : '#fff' }}><FontAwesomeIcon icon={ faCheck } /></div>
                  <div>
                     <span>Running workflow in background</span>
                     <span>Keeps running even after closing the project</span>
                  </div>
               </div>
               <div className="action">
                  <button onClick={ handleClose } className="cancel">Cancel</button>
                  { values.starting ? <button className="stop" onClick={ _=> handleChange('starting') }>
                     <Pause size={ 18 } />
                     <span>Stop Loop</span>
                  </button> : <button onClick={ _=> handleChange('starting') } disabled={ values.minutes < 1 && !inputs.zoneHour }>
                     <Play size={ 18 } />
                     Start Loop
                  </button>}
               </div>
            </div>
         </Main>
      </PopupOverlay>,
      portalTarget
   );
}